import { Button, Flex, Input, Select, Switch } from 'antd';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PresetFormValues } from '../../../lib/types';
import { Delete, PlusBlueWithoutBackground } from '@shared/assets/icons/components';
import { METRIKA_EVENTS } from '@modules/presets/lib/constants';
import { Text } from '@shared/ui/antd';
import { PresetFormItem } from '../form-item';
import styles from '../preset-form.module.scss';

interface AnalyticsSettingsProps {
  control: Control<PresetFormValues>;
  errors: FieldErrors<PresetFormValues>;
  disabled?: boolean;
  onOpenGuide: () => void;
}

export const AnalyticsSettings = ({
  control,
  errors,
  disabled,
  onOpenGuide,
}: AnalyticsSettingsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets.editor.analytics' });
  const enabled = useWatch({ control, name: 'analytics.yandex_metrika.enabled' });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'analytics.yandex_metrika.goals',
  });

  return (
    <div className={styles.fieldsGrid}>
      <div className={styles.fullWidth}>
        <Flex align="center" justify="space-between" gap={16} className={styles.switchCard}>
          <Flex vertical gap={4}>
            <Text variant="sm" fontWeight={600}>
              {t('enabled')}
            </Text>
            <Text variant="xs" className={styles.mutedText}>
              {t('description')}
            </Text>
          </Flex>
          <Controller
            control={control}
            name="analytics.yandex_metrika.enabled"
            render={({ field }) => (
              <Switch checked={field.value} disabled={disabled} onChange={field.onChange} />
            )}
          />
        </Flex>
      </div>

      <PresetFormItem
        label={t('counterId')}
        error={errors.analytics?.yandex_metrika?.counter_id?.message}
        required={enabled}
      >
        <Controller
          control={control}
          name="analytics.yandex_metrika.counter_id"
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              disabled={disabled || !enabled}
              placeholder="12345678"
              inputMode="numeric"
            />
          )}
        />
      </PresetFormItem>

      <Flex align="center" className={styles.guideAction}>
        <Button type="link" onClick={onOpenGuide}>
          {t('howTo')}
        </Button>
      </Flex>

      <div className={styles.fullWidth}>
        <Text variant="sm" fontWeight={600}>
          {t('goals')}
        </Text>
        <Flex vertical gap={8} className={styles.goalsList}>
          {fields.map((field, index) => (
            <div key={field.id} className={styles.goalRow}>
              <Controller
                control={control}
                name={`analytics.yandex_metrika.goals.${index}.event`}
                render={({ field: eventField }) => (
                  <Select
                    {...eventField}
                    disabled={disabled || !enabled}
                    placeholder={t('eventPlaceholder')}
                    status={
                      errors.analytics?.yandex_metrika?.goals?.[index]?.event ? 'error' : undefined
                    }
                    options={METRIKA_EVENTS.map((event) => ({
                      label: t(`events.${event}`),
                      value: event,
                    }))}
                    onChange={eventField.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name={`analytics.yandex_metrika.goals.${index}.goal_id`}
                render={({ field: goalField }) => (
                  <Input
                    {...goalField}
                    disabled={disabled || !enabled}
                    placeholder={t('goalIdPlaceholder')}
                    status={
                      errors.analytics?.yandex_metrika?.goals?.[index]?.goal_id
                        ? 'error'
                        : undefined
                    }
                  />
                )}
              />
              <Button
                danger
                type="text"
                icon={<Delete />}
                aria-label={t('removeGoal')}
                disabled={disabled || !enabled}
                className={styles.iconButton}
                onClick={() => remove(index)}
              />
            </div>
          ))}

          <Button
            type="dashed"
            icon={<PlusBlueWithoutBackground />}
            disabled={disabled || !enabled}
            className={styles.addButton}
            onClick={() => append({ event: 'WIDGET_SHOWN', goal_id: '' })}
          >
            {t('addGoal')}
          </Button>
        </Flex>
      </div>
    </div>
  );
};
